import React, { useRef, useState, useEffect } from "react";
import { motion, useMotionValue } from "framer-motion";
import { Mail, MessageSquare, Check, Send } from "lucide-react";
import { sounds } from "@/lib/sounds";

export default function StickyNote({ note, boardRef, onComplete, onExpand, onPositionChange, onBringFront, editing, onEditStart, onSaveText, onEditEnd }) {
  const x = useMotionValue(note.x);
  const y = useMotionValue(note.y);
  const moved = useRef(false);
  const clickTimer = useRef(null);
  const taRef = useRef(null);
  const [text, setText] = useState(note.body || "");

  useEffect(() => { if (!editing) setText(note.body || ""); }, [note.body, editing]);
  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      const v = taRef.current.value;
      taRef.current.setSelectionRange(v.length, v.length);
    }
  }, [editing]);
  useEffect(() => () => clearTimeout(clickTimer.current), []);

  const doComplete = () => { sounds.peelOff(); onComplete(note.id); };
  const saveText = () => { onSaveText(note.id, { body: text }); onEditEnd(); };

  const ChannelIcon = note.channel === "sms" ? MessageSquare : note.channel === "telegram" ? Send : Mail;
  const usePin = (note.id.charCodeAt(0) % 2) === 0;
  const isManual = note.channel === "manual";

  return (
    <motion.div
      data-testid={`note-${note.id}`}
      className={`note-${note.color} absolute w-[190px] sm:w-[210px] rounded-[3px] paper-shadow grain select-none ${editing ? "cursor-text" : "cursor-grab active:cursor-grabbing"}`}
      style={{ x, y, rotate: note.rotation, zIndex: note.z_index, touchAction: "none", transformOrigin: "bottom center", transformPerspective: 900 }}
      drag={!editing}
      dragConstraints={boardRef}
      dragMomentum={false}
      whileDrag={{ scale: 1.06, rotate: 0, zIndex: 9999, boxShadow: "0 18px 34px rgba(0,0,0,.3)" }}
      exit={{ opacity: 0, rotateX: -82, rotateZ: 9, scale: 0.9, boxShadow: "0 28px 42px rgba(0,0,0,.32)", transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] } }}
      onDragStart={() => { moved.current = false; sounds.shuffle(); onBringFront(note.id); }}
      onDrag={(e, info) => { if (Math.abs(info.offset.x) > 6 || Math.abs(info.offset.y) > 6) moved.current = true; }}
      onDragEnd={(e, info) => {
        if (info.offset.x > 175 && info.velocity.x > 120) { doComplete(); return; }
        onPositionChange(note.id, x.get(), y.get());
      }}
      onClick={() => {
        if (editing || moved.current) return;
        clearTimeout(clickTimer.current);
        clickTimer.current = setTimeout(() => onExpand(note), 200);
      }}
      onDoubleClick={(e) => { e.stopPropagation(); clearTimeout(clickTimer.current); onEditStart(note.id); }}
    >
      {usePin ? <span className="thumbtack" /> : <span className="washi" />}
      <div className="p-4 pt-6">
        {!isManual && (
          <div className="flex items-center gap-1.5 mb-1">
            <ChannelIcon className="w-3.5 h-3.5 text-[#5a5148]" strokeWidth={2.4} />
            <span className="font-note text-[13px] font-bold text-[#4a4238] truncate">{note.sender_name || note.sender}</span>
          </div>
        )}
        {note.subject ? <p className="font-note text-[15px] font-bold text-[#2b2620] leading-tight mb-1">{note.subject}</p> : null}
        {editing ? (
          <textarea
            ref={taRef}
            data-testid={`note-edit-${note.id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={saveText}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") { setText(note.body || ""); onEditEnd(); }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { saveText(); }
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            rows={4}
            placeholder="Type your note…"
            className="w-full bg-transparent outline-none resize-none font-note text-[14px] text-[#2b2620] leading-snug placeholder:text-[#a89a86]"
          />
        ) : (
          <p className="font-note text-[14px] text-[#2b2620] leading-snug line-clamp-4 whitespace-pre-wrap break-words min-h-[1.2em]">
            {note.body || <span className="text-[#a89a86] italic">Empty — double-tap to write</span>}
          </p>
        )}
      </div>
      {!editing && (
        <button
          data-testid={`note-done-${note.id}`}
          onClick={(e) => { e.stopPropagation(); doComplete(); }}
          className="absolute bottom-2 right-2 h-8 pl-2 pr-3 rounded-full bg-white/85 hover:bg-[#34c759] hover:text-white text-[#4a4238] flex items-center gap-1 text-[11px] font-display font-bold paper-shadow transition-colors"
          title="Peel this note off the board"
        >
          <Check className="w-4 h-4" strokeWidth={3} /> Done
        </button>
      )}
    </motion.div>
  );
}
